import {
  AdminEnterpriseModel,
  AdminEnterprisesCollection,
  SearchActiveProject,
  SearchActiveProjectsCollection,
  ProjectViewUser,
  ProjectViewUsersCollection,
} from '@freelancer/datastore/collections';
import { SearchQueryParams } from '@freelancer/datastore/core';
import { assertNever, isDefined } from '@freelancer/utils';
import { ProjectTypeApi } from 'api-typings/projects/projects';
import { addSearchTransformer } from './document-creators';

export function addSearchTransformers(authUid: number): void {
  addSearchTransformer<SearchActiveProjectsCollection>(
    'searchActiveProjects',
    searchActiveProjectsFilter,
  );
  addSearchTransformer<AdminEnterprisesCollection>(
    'adminEnterprises',
    searchAdminEnterprisesFilter,
  );
  addSearchTransformer<ProjectViewUsersCollection>(
    'projectViewUsers',
    searchProjectViewUserFilter,
  );
}

function searchActiveProjectsFilter(
  documents: readonly SearchActiveProject[],
  {
    query,
    minHourlyRate,
    maxHourlyRate,
    minFixedPrice,
    maxFixedPrice,
  }: SearchQueryParams,
) {
  return documents.filter(project => {
    let result = true;

    // Find the query keywords from project title and description.
    if (isDefined(query)) {
      if (!(typeof query === 'string')) {
        throw new Error(
          "'query' field in search query params must be a string.",
        );
      }
      result =
        result &&
        (project.title.includes(query) || project.description.includes(query));
    }

    // Filter out project in the budget range.
    // The legacy API used by SearchActiveProjectsCollection doesn't compare
    // the budget range filter with the project budget range. Instead, it
    // compares the budget range filter with the USD bid average in Elastic Search.
    if (isDefined(project.bidStats.bidAvg)) {
      // When we are unsure about the project currency exchange rate,
      // let's assume it as USD.
      const bidAvg =
        project.bidStats.bidAvg * (project.currency.exchangeRate ?? 1);
      switch (project.type) {
        case ProjectTypeApi.HOURLY:
          result =
            result &&
            (!isDefined(minHourlyRate) || minHourlyRate <= bidAvg) &&
            (!isDefined(maxHourlyRate) || bidAvg <= maxHourlyRate);
          break;
        case ProjectTypeApi.FIXED:
          result =
            result &&
            (!isDefined(minFixedPrice) || minFixedPrice <= bidAvg) &&
            (!isDefined(maxFixedPrice) || bidAvg <= maxFixedPrice);
          break;
        default:
          assertNever(project.type);
      }
    }

    return result;
  });
}

function searchAdminEnterprisesFilter(
  documents: readonly AdminEnterpriseModel[],
  { query }: SearchQueryParams,
) {
  if (isDefined(query) && typeof query === 'string') {
    return documents.filter(
      enterprise =>
        enterprise.seoUrl?.includes(query) || enterprise.name.includes(query),
    );
  }
  return documents;
}

function searchProjectViewUserFilter(
  documents: readonly ProjectViewUser[],
  { query }: SearchQueryParams,
) {
  if (isDefined(query) && typeof query === 'string') {
    return documents.filter(user => user.username.includes(query));
  }
  return documents;
}
